import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import jwtDecode from "jwt-decode";
import SkillzAdminLogin from "./SkillzAdminLogin";
import SkillzAdminDashboard from "./SkillzAdminDashboard";

const SKILLZ_ADMIN_TOKEN_KEY = "skillz-admin-token";

type SkillzAdminToken = {
  username?: string;
  role?: string;
  expires?: number;
};

const getStoredToken = () => {
  try {
    return localStorage.getItem(SKILLZ_ADMIN_TOKEN_KEY);
  } catch {
    return null;
  }
};

const SkillzAdminRouter = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(() => getStoredToken());

  const decoded = useMemo(() => {
    if (!token) return null;
    try {
      return jwtDecode<SkillzAdminToken>(token);
    } catch {
      return null;
    }
  }, [token]);

  const isTokenValid = useMemo(() => {
    if (!decoded) return false;
    if (!decoded.expires) return true;
    return decoded.expires > Math.floor(Date.now() / 1000);
  }, [decoded]);

  useEffect(() => {
    if (!isTokenValid && token) {
      try {
        localStorage.removeItem(SKILLZ_ADMIN_TOKEN_KEY);
      } catch {
        // ignore storage errors
      }
      setToken(null);
    }
  }, [isTokenValid, token]);

  useEffect(() => {
    if (!isTokenValid && location.pathname !== "/skillz-admin/login") {
      navigate("/skillz-admin/login");
    }
    if (isTokenValid && location.pathname === "/skillz-admin/login") {
      navigate("/skillz-admin");
    }
  }, [isTokenValid, location.pathname, navigate]);

  const handleLogin = useCallback((newToken: string) => {
    try {
      localStorage.setItem(SKILLZ_ADMIN_TOKEN_KEY, newToken);
    } catch {
      // ignore storage errors
    }
    setToken(newToken);
    navigate("/skillz-admin");
  }, [navigate]);

  const handleLogout = useCallback(() => {
    try {
      localStorage.removeItem(SKILLZ_ADMIN_TOKEN_KEY);
    } catch {
      // ignore storage errors
    }
    setToken(null);
    navigate("/skillz-admin/login");
  }, [navigate]);

  return (
    <Routes>
      <Route path="login" element={<SkillzAdminLogin onLogin={handleLogin} />} />
      <Route
        index
        element={
          isTokenValid ? (
            <SkillzAdminDashboard token={token} onLogout={handleLogout} />
          ) : (
            <Navigate to="/skillz-admin/login" replace />
          )
        }
      />
      <Route path="*" element={<Navigate to="/skillz-admin" replace />} />
    </Routes>
  );
};

export default SkillzAdminRouter;
